import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { DeviceStatus } from '@/api/user/getUsers.ts'

export interface IProps {
    battery?: number
    status?: DeviceStatus
    className?: string
    showPercentage?: boolean
}

const BatteryStatusBadge: React.FC<IProps> = ({ 
    className, 
    battery = 0, 
    status, 
    showPercentage = true 
}) => {
    const { t } = useTranslation()

    const getBatteryColor = useMemo(() => {
        if (status === DeviceStatus.EMERGENCY) {
            return '#DC2626' // 긴급 - 빨간색
        }
        if (status === DeviceStatus.CHARGING) {
            return '#06B6D4' // 충전 중 - 청록색
        }
        if (status === DeviceStatus.OFFLINE) {
            return '#6B7280' // 꺼짐 - 회색
        }
        // ONLINE 상태일 때 배터리 레벨에 따른 색상
        if (battery >= 60) return '#10B981' // 충분 - 초록색
        if (battery >= 30) return '#F59E0B' // 보통 - 노란색
        if (battery >= 15) return '#EF4444' // 낮음 - 빨간색
        return '#7F1D1D' // 매우 낮음 - 어두운 빨간색
    }, [battery, status])

    const getBatteryIcon = useMemo(() => {
        if (status === DeviceStatus.EMERGENCY) {
            return (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    {/* 경고 삼각형 아이콘 */}
                    <path 
                        d="M12 2L2 20h20L12 2z" 
                        fill={getBatteryColor}
                        stroke={getBatteryColor}
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                    <path 
                        d="M12 9v4M12 17h.01" 
                        stroke="white"
                        strokeWidth="2"
                        strokeLinecap="round"
                    />
                </svg>
            )
        }
        
        if (status === DeviceStatus.CHARGING) {
            return (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    {/* 큰 번개 모양 충전 아이콘 */}
                    <path 
                        d="M13 3L4 14H11L10 21L19 10H12L13 3Z" 
                        fill={getBatteryColor}
                        stroke={getBatteryColor}
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            )
        }

        // 일반 배터리 상태
        const fillWidth = Math.max(2, (battery / 100) * 11) // 최소 2px는 보이도록
        return (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                {/* 배터리 외곽 */}
                <rect x="4" y="7" width="13" height="10" rx="1" fill="none" stroke={getBatteryColor} strokeWidth="2"/>
                {/* 배터리 + 단자 */}
                <rect x="18" y="10" width="2" height="4" rx="1" fill={getBatteryColor}/>
                {/* 배터리 충전량 */}
                <rect x="5" y="8" width={fillWidth} height="8" rx="0.5" fill={getBatteryColor}/>
            </svg>
        )
    }, [battery, status, getBatteryColor])

    const getStatusText = useMemo(() => {
        if (status === DeviceStatus.CHARGING) {
            return t('users.detail.location.device.status.charging')
        }
        if (status === DeviceStatus.OFFLINE) {
            return t('users.detail.location.device.status.turnOff')
        }
        if (status === DeviceStatus.EMERGENCY) {
            return t('notification.v2.battery.emergency')
        }
        if (status === DeviceStatus.ONLINE) {
            return t('notification.v2.battery.inUse')
        }
        return t('wandering.status.unknown')
    }, [status, t])

    return (
        <div className={`${className} flex items-center gap-1 px-2 py-1 rounded-md bg-gray-50 border`}>
            {getBatteryIcon}
            <div className="flex flex-col text-xs">
                <span style={{ color: getBatteryColor }} className="font-medium">
                    {getStatusText}
                </span>
                {showPercentage && battery !== undefined && battery !== null && (
                    <span className="text-gray-600">
                        {battery}%
                    </span>
                )}
            </div>
        </div>
    )
}

export default BatteryStatusBadge 